import {ApolloLink, Observable} from '@apollo/client';
import stitchSubLinksResults from 'src/internal/revamp/links/stitchPartialResults';
import {reportRuntimeError} from 'src/internal/revamp/telemetry/internalReporting';

import type {DocumentNode, ExecutionResult} from 'graphql';
import type {
  InternalOperation,
  InternalOperationContext,
} from 'src/internal/revamp/apollo/types';
import type {OperationVariables} from 'src/internal/revamp/graphql/types';
import type {
  FailedResult,
  SuccessResult,
} from 'src/internal/revamp/links/stitchPartialResults';

export interface SplitQueryExecution {
  query: DocumentNode;
  link: ApolloLink;
  variables: OperationVariables;
  containsNonNullableFields: boolean;
}

function createExecutionPromise(
  execution: SplitQueryExecution,
  operationContext: InternalOperationContext,
): {
  promise: Promise<SuccessResult | FailedResult>;
  unsubscribe: () => void;
} {
  const {query, link, variables, containsNonNullableFields} = execution;

  let subscription: {unsubscribe: () => void} | null = null;

  const promise = new Promise<SuccessResult | FailedResult>(
    function apolloLinkExecution(resolve) {
      try {
        subscription = ApolloLink.execute(link, {
          query,
          variables,
          context: operationContext,
        }).subscribe({
          next: function splitLinkNext(result) {
            const enhancedResult: SuccessResult = {
              success: true,
              linkResult: result,
              extensions: {
                containsNonNullableFields,
              },
            };
            resolve(enhancedResult);
          },
          error: function splitLinkError(error: unknown) {
            resolve({
              success: false,
              error,
            });
          },
        });
      } catch (error: unknown) {
        reportRuntimeError(operationContext.observabilityFns, error);

        resolve({
          success: false,
          error,
        });
      }
    },
  );

  const unsubscribe = (): void => {
    if (subscription) {
      subscription.unsubscribe();
      subscription = null;
    }
  };

  return {promise, unsubscribe};
}

/**
 * Creates an Observable that executes multiple split queries and emits the combined result.
 * This function handles the common pattern used in multiple Apollo links.
 */
export default function createSplitLinkObservable(
  executionDefinitions: ReadonlyArray<SplitQueryExecution>,
  operation: InternalOperation,
): Observable<ExecutionResult> {
  return new Observable(function splitLinkSubscriptionFn(observer) {
    const operationContext = operation.getContext();

    const executions = executionDefinitions.map((execution) =>
      createExecutionPromise(execution, operationContext),
    );

    const promises = executions.map((execution) => execution.promise);

    Promise.all(promises)
      .then(function subLinksResolved(results) {
        const stitchedResult = stitchSubLinksResults(results);

        if (stitchedResult.success) {
          observer.next(stitchedResult.result);
        } else {
          observer.error(stitchedResult.error);
        }

        observer.complete();
      })
      .catch(function runTimeError(error) {
        observer.error(error);
      });

    return function splitObservableCleanup() {
      executions.forEach((execution) => {
        execution.unsubscribe();
      });
    };
  });
}
